import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useUser } from '@clerk/clerk-react';
import { Server, Database, Wifi, Globe, Shield, HardDrive } from 'lucide-react';

// Import all components
import { TopStatusBar, MainHeader } from './components/HeaderComponents';
import { HeroVideoSection, PortfolioSection } from './components/ContentSections';
import { ClientDashboard } from './components/ClientDashboard';
import { ContactSection, Footer } from './components/ContactFooter';

// Import the FIXED WidgetComponents with improved deduplication
import { ChatWidget, SystemStatusPanel, ScrollToTopButton, NotificationToast } from './components/WidgetComponents';

// Import custom hooks
import { useTheme, useServerStatus, useContactForm, usePortfolioAnimations } from './components/CustomHooks';

const App = () => {
  // Use custom hooks
  const { isDarkMode, toggleTheme, theme } = useTheme();
  const { serverStatus, uptimeData, services: serverServices, servicesLoading } = useServerStatus();
  const { user, isLoaded: clerkLoaded } = useUser();
  const { contactForm, notification, setNotification, handleContactFormChange, handleContactSubmit } = useContactForm();

  // State for UI components
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusClosing, setStatusClosing] = useState(false);
  const [showHeroText, setShowHeroText] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showSignInSuccess, setShowSignInSuccess] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginClosing, setLoginClosing] = useState(false);

  const closeLogin = () => {
    setLoginClosing(true);
    setTimeout(() => { setShowLogin(false); setLoginClosing(false); }, 200);
  };
  const [showDashboard, setShowDashboard] = useState(() => window.location.hash === '#dashboard' || window.location.hash.startsWith('#portal-'));
  const [dashboardTransitioning, setDashboardTransitioning] = useState(false);
  const [dashboardVisible, setDashboardVisible] = useState(window.location.hash === '#dashboard' || window.location.hash.startsWith('#portal-'));

  const openDashboard = () => {
    setDashboardTransitioning(true);
    setTimeout(() => {
      setShowDashboard(true);
      window.location.hash = 'dashboard';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => { setDashboardVisible(true); setDashboardTransitioning(false); }, 50);
    }, 300);
  };

  const isDashboardHash = (hash) => hash === '#dashboard' || hash.startsWith('#portal-');

  const closeDashboard = () => {
    setDashboardVisible(false);
    setDashboardTransitioning(true);
    setTimeout(() => {
      setShowDashboard(false);
      setDashboardTransitioning(false);
      // Clear any portal/dashboard hash so a refresh lands on the homepage
      if (window.location.hash && isDashboardHash(window.location.hash)) {
        window.history.pushState('', document.title, window.location.pathname);
      }
    }, 350);
  };

  const triggerSignInSuccess = () => {
    setShowSignInSuccess(true);
    // Unmount after 4.3s — the scToastAnim CSS animation is 4s,
    // fade-out runs at the end, this gives it time to finish before removal
    setTimeout(() => setShowSignInSuccess(false), 4300);
    openDashboard();
  };

  const triggerSignOutSuccess = () => {
    if (window.__scSignOutToast) window.__scSignOutToast();
  };

  // Restore scroll position on page refresh (homepage only)
  useEffect(() => {
    window.history.scrollRestoration = showDashboard ? 'manual' : 'auto';
  }, [showDashboard]);
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash;
      if (isDashboardHash(hash)) {
        // Back button navigated to dashboard — show it with the fade-in animation
        setDashboardTransitioning(false);
        setShowDashboard(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        requestAnimationFrame(() => requestAnimationFrame(() => setDashboardVisible(true)));
      } else if (hash === '' || hash === '#') {
        // Back button navigated away from dashboard — hide it with animation
        setDashboardVisible(false);
        setDashboardTransitioning(true);
        setTimeout(() => {
          setShowDashboard(false);
          setDashboardTransitioning(false);
        }, 350);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Only redirect away from dashboard once Clerk has confirmed no user
  useEffect(() => {
    if (!clerkLoaded) return;
    if (!user && showDashboard) {
      const t = setTimeout(() => {
        if (!user) setShowDashboard(false);
      }, 500);
      return () => clearTimeout(t);
    }
  }, [user, clerkLoaded]);

  // Portfolio data
  const [portfolioItems] = useState([
    {
      id: 1,
      title: "Chelsea FC",
      description: "A longstanding creative relationship with Chelsea FC, supporting both the Men's and Women's teams with high-quality digital and print media. Our work spans the full creative spectrum — from campaign artworking and image retouching for match-day and promotional content, to producing polished visual assets used across social media, print publications and club communications. Every piece of work is produced to the exacting standards you'd expect from one of the world's most recognised football clubs, with an eye for detail that ensures the Chelsea brand is always represented at its very best.",
      images: [
        "https://spiralcreations.co.uk/cfc_stadium.jpg"
      ],
      tags: ["Digital Creative", "Artworking", "Image Retouching", "Print Media"],
      featured: true,
      logo: "https://spiralcreations.co.uk/Chelsea_FC.svg"
    },
    {
      id: 2,
      title: "West Ham United FC",
      description: "An onsite presence at London Stadium for matchdays and preparations throughout the week, our team is embedded with West Ham United for the full season. We manage a comprehensive stadium digital operation spanning over 300 screens — covering concourses, executive boxes, lounges and kiosks — delivering real-time content, fan engagement campaigns and branded digital signage. From IPTV installations and ongoing operations to all stadium screen and kiosk content media, we are a trusted and integral part of the club's matchday experience week in, week out.",
      images: ["https://spiralcreations.co.uk/wh_stadium.jpg"],
      tags: ["IPTV Installation & Operation", "300+ Screen Management", "Fan Engagement", "Digital Signage", "Kiosk Content"],
      featured: true,
      copyright: true,
      logo: "https://spiralcreations.co.uk/West_Ham_United_FC.png"
    },
    {
      id: 3,
      title: "Tottenham Hotspur FC",
      description: "Supporting Tottenham Hotspur with a range of digital creative services, including the production of animated assets, digital content for club communications and email newsletter templates. We also contribute to SpursPLAY TV, Tottenham's own streaming platform, providing polished visual content that keeps supporters engaged on and off matchday.",
      images: ["https://spiralcreations.co.uk/spursplay_splash.jpg"],
      tags: ["Digital Assets", "Animation", "Email Newsletter Templates", "SpursPLAY TV Content"],
      featured: true,
      logo: "https://spiralcreations.co.uk/tottenham.svg"
    },
    {
      id: 4,
      title: "Fulham FC",
      description: "An onsite presence at Craven Cottage every matchday and throughout the week, our team works with Fulham FC to keep the stadium's digital operation running at its best. We handle highlights editing for post-match content, scoreboard media management, and ongoing stadium screen maintenance — ensuring everything is prepared, tested and performing flawlessly well before the first fan walks through the gate.",
      images: ["https://spiralcreations.co.uk/ffc_stadium.jpg"],
      tags: ["Highlights Editing", "Scoreboard Media", "Stadium Screen Maintenance", "Digital Creative"],
      featured: false,
      copyright: true,
      logo: "https://spiralcreations.co.uk/Fulham_FC.png"
    },
    {
      id: 17,
      title: "Bromley FC",
      description: "Working with Bromley FC to manufacture and deliver full perimeter advertising boards for the pitch surround. From production through to installation, we handle the entire process — ensuring every board is built to specification, delivered on time and positioned perfectly around the pitch ahead of each fixture. The result is a clean, professional pitchside presence that gives brands maximum visibility throughout every home game. A straightforward service done properly, from start to finish.",
      images: [
        "https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=2070&q=80"
      ],
      tags: ["Perimeter Advertising Boards", "Pitchside Branding", "Match Day Signage"],
      featured: false,
      logo: "/bromley_fc.svg"
    },
    {
      id: 18,
      title: "NFL",
      description: "Working alongside the Jacksonville Jaguars as they brought American Football to London, we created digital graphics that captured the scale and spectacle of one of sport's biggest global brands. From promotional assets to event graphics, our work helped bring the NFL experience to UK audiences — contributing to a production that had to meet the exacting standards the league is known for worldwide. A proud moment to be involved with one of the most recognised sports organisations on the planet.",
      images: [
        "https://images.unsplash.com/photo-1566577739112-5180d4bf9390?auto=format&fit=crop&w=2070&q=80"
      ],
      tags: ["Digital Graphics", "Jacksonville Jaguars", "Event Assets", "NFL London"],
      featured: false,
      logo: "/nfl.png"
    }
  ]);

  const [liveEventsPortfolioItems] = useState([
    {
      id: 5,
      title: "Guns N' Roses",
      description: "When Guns N' Roses took over London Stadium, we were right there in the thick of it. Brought in to manage the full digital operation for one of the biggest live shows the venue has ever seen, our team ran IPTV operations across over 300 screens throughout the venue — from concourses and bars to executive areas — ensuring every fan in the building was immersed in the show regardless of where they stood. We also operated Europe's largest outdoor screen system, broadcasting stunning visuals and live footage that elevated the atmosphere beyond the stage itself. A truly unforgettable production that brought the fan experience to a whole new level.",
      images: ["/gnr.jpg"],
      tags: ["IPTV Operations", "Stadium Broadcasting", "Outdoor Screen Systems", "Live Concert Technology"],
      featured: true,
      copyright: true,
      logo: "/gnr_logo.png"
    },
    {
      id: 6,
      title: "Olympics & Paralympics",
      description: "Trusted to work onsite across the Olympics and Paralympics, Spiral Creations manages the full digital operation throughout every single day of the games — from the opening ceremony through to the final event. Our team is embedded within the venue for the entire duration, ensuring seamless delivery of broadcast graphics, ceremonial displays and athlete presentation content with pinpoint precision. Advert playouts, sponsor content and live event graphics are all timed to the second, reflecting the world-class standard these global events demand. When the whole world is watching, there is no margin for error — and we deliver exactly that.",
      images: ["/athletics.jpg"],
      tags: ["Full Event Management", "Broadcast Graphics", "Advert Playout Timing", "Ceremonial Displays", "Athlete Presentation"],
      featured: true,
      copyright: true,
      logo: "https://affairscloud.com/assets/uploads/2017/08/world-championship.jpg"
    }
  ]);

  const [promotionsPortfolioItems] = useState([
    {
      id: 9,
      title: "EA Sports - FIFA",
      description: "A fast-paced creative relationship with EA Sports, built around the demands of one of the world's biggest gaming franchises. We produce custom printed football kits for FIFA game releases and events, manage professional event setup and brand activations, and deliver comprehensive promotional campaigns around major FIFA launches. What sets us apart is our ability to turn around time-sensitive requests within hours — when EA Sports needs something done at short notice, we deliver without compromise.",
      images: ["/fifa_shirt_print.png"],
      tags: ["Custom Kit Printing", "Event Setup & Activation", "FIFA Launch Campaigns", "Fast Turnaround"],
      featured: true,
      logo: "https://static.wikia.nocookie.net/logopedia/images/b/b4/EA_Sports_%28Flat%29.svg"
    },
    {
      id: 10,
      title: "Halfords",
      description: "A high-volume, fast-turnaround creative relationship with Halfords, one of the UK's most recognised retail brands. We produce large format signage for exhibitions, store openings and in-store campaigns — handling everything from artwork through to print and delivery. Speed and consistency are everything in retail, and our ability to turn around large format work at pace without compromising on quality is exactly what Halfords demands. Whether it's a nationwide rollout or a last-minute campaign push, we get it done.",
      images: [
        "https://blog.halfords.com/wp-content/uploads/2021/11/57A5292-1024x683.jpg"
      ],
      tags: ["Large Format Signage", "Exhibition Graphics", "In-Store Campaigns", "Fast Turnaround"],
      featured: true,
      logo: "https://images.seeklogo.com/logo-png/51/2/halfords-logo-png_seeklogo-518458.png"
    },
    {
      id: 13,
      title: "Superdrug",
      description: "Working with Superdrug to deliver in-store signage at lightning speed across their UK store network. Retail moves fast, and Superdrug is no exception — our team produces and turns around in-store graphics, promotional signage and campaign materials at a pace that keeps up with the demands of one of the UK's most active health and beauty retailers. Tight deadlines, high standards and consistent quality are the expectation — and it's a challenge we consistently meet.",
      images: [
        "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?auto=format&fit=crop&w=2070&q=80"
      ],
      tags: ["In-Store Signage", "Retail Graphics", "Campaign Materials", "Fast Turnaround"],
      featured: false,
      logo: "/superdrug_logo.png",
      logoCover: true
    },
    {
      id: 11,
      title: "Jewlr",
      description: "Supplying Jewlr with their full printed catalogues, from artwork and pre-press through to print and delivery. Jewlr is a fast-moving personalised jewellery brand with high expectations — catalogues need to look exceptional and land on time, every time. Our team manages the entire production process, ensuring print-ready files are produced to the highest standard and deadlines are met with ease. A reliable, precise service for a brand where presentation is everything.",
      images: [
        "https://assets.jewlr.com/images/info/jewlr/gemstones/gemstone.png"
      ],
      tags: ["Catalogue Print", "Artwork & Pre-Press", "Print Production", "Deadline Delivery"],
      featured: true,
      logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTRq7yUR2A6ehwTMksDEMGwNqoxhbS9_IjtGQ&s"
    },
    {
      id: 12,
      title: "Calibre Routex Group",
      description: "Calibre Routex Group came to us needing a strong digital presence and smarter internal tooling. We designed and built their website from the ground up, creating a professional platform that reflects the scale and reliability of their logistics operation. Alongside this, we developed a bespoke iPad application to streamline the processing of incoming and outgoing vehicles across the business — replacing manual workflows with a fast, intuitive system that keeps operations running smoothly and accurately. A project that combined creative design with practical, purpose-built technology.",
      images: ["/calibre.png"],
      tags: ["Website Design & Build", "iPad App Development", "Vehicle Processing System", "Logistics Technology"],
      featured: false,
      logo: "/cal_logo.svg",
      whiteLogo: true,
      copyright: true
    },
    {
      id: 14,
      title: "Sinclair London",
      description: "Producing brochures and artworking model images for Sinclair London, including contributions to the prestigious Grammy Awards programme. Every piece of work demands the kind of precision and polish you'd expect from a luxury brand appearing on one of music's biggest global stages. From careful image retouching and artworking through to the finished printed brochure, we handle the process end to end — delivering work that reflects the sophistication and attention to detail that Sinclair London is known for.",
      images: [
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=2070&q=80"
      ],
      tags: ["Digital Brochures", "Model Image Artworking", "Print Production", "Grammy Awards Programme"],
      featured: false,
      logo: "/sinclair.webp"
    }
  ]);

  const [musicPortfolioItems] = useState([
    {
      id: 7,
      title: "P-Money",
      description: "A close creative relationship with UK Grime artist P-Money, spanning some of the most visually defining work of his career. We worked alongside him from start to finish on every project — staying in close contact throughout each creative process to ensure the vision was right at every stage. Our work together covers several album artworks, bringing his sound to life through bold, considered design that felt true to who he is as an artist. Our involvement extended to clothing design and print — producing garments that carried the same visual identity as his music — as well as featuring in music video productions, contributing to the visual storytelling behind his releases. A collaboration built on trust, open communication and a shared passion for getting it right.",
      images: [
        "https://assets.amsterdam-dance-event.nl/images/images/_1200x630_crop_center-center_none/2591276/P-Money.webp"
      ],
      tags: ["Album Artwork Design", "Clothing Design & Print", "Music Video Features"],
      featured: true,
      logo: "https://images.genius.com/72cb3af0c3a28dbb44765a480f30dcc8.1000x1000x1.jpg"
    },
    {
      id: 8,
      title: "Skepta",
      description: "We put on events with Skepta — working directly alongside one of UK Grime's most iconic artists to bring his world to life in real spaces. At the height of his rise to global recognition, we established and drove the Skepta Street Team — a fan-led grassroots movement that put boots on the ground across the UK, building authentic buzz and real-world visibility where it mattered most. We promoted and delivered multiple album launch parties and celebratory events, each one crafted to match the moment and the music — from intimate gatherings to full-scale promotional events that kept his fanbase energised and engaged throughout his career. Raw, real and rooted in culture — exactly how it should be.",
      images: [
        "/skepta.png"
      ],
      tags: ["Street Team Launch", "Fan Engagement", "Album Launch Promotion", "Grassroots Marketing"],
      featured: true,
      logo: "https://i.namu.wiki/i/AXzXdyAgHswIpN7N6pAsRsKJlGGiqTs77w_-_oPzmCN-GsjOAlQtM4pUM6p6yR-qPXhNcuFJUctiMS21OEoiMw.webp"
    }
  ]);

  // Use portfolio animations hook
  const { hoveredItem, setHoveredItem, currentImageIndex, particles, createParticles } = usePortfolioAnimations(
    portfolioItems, 
    liveEventsPortfolioItems, 
    promotionsPortfolioItems, 
    musicPortfolioItems
  );

  // Services with icons mapped
  const services = serverServices.map(service => ({
    ...service,
    icon: service.name === 'Web Server' ? Server :
          service.name === 'Database' ? Database :
          service.name === 'API Gateway' ? Wifi :
          service.name === 'CDN' ? Globe :
          service.name === 'Security' ? Shield :
          service.name === 'Backup' ? HardDrive : Server
  }));

  // Handle menu and panel toggles
  const handleMenuToggle = () => {
    if (isMenuOpen) {
      setMenuClosing(true);
      setIsMenuOpen(false);
      setTimeout(() => setMenuClosing(false), 300);
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleStatusToggle = () => {
    if (statusOpen) {
      setStatusClosing(true);
      setStatusOpen(false);
      setTimeout(() => setStatusClosing(false), 300);
    } else {
      setStatusOpen(true);
    }
  };

  // Navigation functions
  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleContactClick = () => {
    handleMenuToggle();
    setTimeout(() => {
      document.getElementById('contact').scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 300);
  };

  const handleServicesClick = () => {
    handleMenuToggle();
    setTimeout(() => {
      document.getElementById('services').scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 300);
  };

  const handleProjectsClick = () => {
    handleMenuToggle();
    setTimeout(() => {
      document.getElementById('projects').scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 300);
  };

  // Effects
  useEffect(() => {
    document.title = 'Spiral Creations';
    
    // Add viewport meta tag
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    
    // Set theme colors
    const setThemeColor = (color) => {
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.name = 'theme-color';
        document.head.appendChild(themeColorMeta);
      }
      themeColorMeta.content = color;
    };

    const themeColor = isDarkMode ? '#1e293b' : '#f8fafc';
    setThemeColor(themeColor);
  }, [isDarkMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusOpen && !event.target.closest('.metrics-panel') && !event.target.closest('.uptime-button') && !event.target.closest('.services-button')) {
        handleStatusToggle();
      }
    };

    if (statusOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [statusOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (showDashboard) return; // don't run while dashboard is showing
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('section-heading-hidden');
            entry.target.classList.add('section-heading-visible');
          } else {
            entry.target.classList.remove('section-heading-visible');
            entry.target.classList.add('section-heading-hidden');
          }
        });
      },
      { threshold: 0.5, rootMargin: '0px 0px -60px 0px' }
    );
    const t = setTimeout(() => {
      document.querySelectorAll('.section-heading').forEach(el => observer.observe(el));
    }, 50);
    return () => { observer.disconnect(); clearTimeout(t); };
  }, [isDarkMode, showDashboard]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white' : 'text-slate-900 light-pattern'} relative`}>
      {/* All the existing styles */}
      <style>{`
        * {
          box-sizing: border-box;
        }
        
        html, body {
          margin: 0;
          padding: 0;
          background-color: ${isDarkMode ? '#0f172a' : '#f1f5f9'} !important;
          scroll-behavior: smooth;
        }
        
        /* Light mode professional pattern background */
        ${!isDarkMode ? `
        .light-pattern {
          background: #f1f5f9 !important;
        }
        .light-pattern::before {
          content: '';
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image:
            linear-gradient(rgba(148,163,184,0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148,163,184,0.12) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
          z-index: 0;
        }
        .light-pattern::after {
          content: '';
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background:
            radial-gradient(ellipse 80% 60% at 10% 0%, rgba(235,0,139,0.06) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 90% 100%, rgba(99,102,241,0.05) 0%, transparent 60%);
          pointer-events: none;
          z-index: 0;
        }
        ` : ''}
        
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&display=swap');

        .section-heading {
          font-family: 'Barlow Condensed', sans-serif !important;
          font-weight: 800 !important;
          letter-spacing: 0.15em !important;
        }
        .section-heading-hidden {
          opacity: 0;
          transform: translateY(40px) scaleX(0.95);
          filter: blur(4px);
          transition: opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1), filter 0.7s ease;
        }
        .section-heading-visible {
          opacity: 1;
          transform: translateY(0) scaleX(1);
          filter: blur(0);
          transition: opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1), filter 0.7s ease;
        }

        @keyframes logo-scroll-left {
          0% { transform: translateX(0) translateZ(0); }
          100% { transform: translateX(-50%) translateZ(0); }
        }
        @keyframes logo-scroll-right {
          0% { transform: translateX(-50%) translateZ(0); }
          100% { transform: translateX(0) translateZ(0); }
        }
        .logo-row-left {
          animation: logo-scroll-left 18s linear infinite;
          width: max-content;
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .logo-row-right {
          animation: logo-scroll-right 18s linear infinite;
          width: max-content;
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        @keyframes toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(-16px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        @keyframes trusted-fade-in {
          from { opacity: 0; letter-spacing: 0.8em; }
          to { opacity: 1; letter-spacing: 0.4em; }
        }
        @keyframes giants-fade-in {
          from { opacity: 0; transform: translateY(20px) scaleX(0.92); filter: blur(8px); }
          to { opacity: 1; transform: translateY(0) scaleX(1); filter: blur(0); }
        }

        @keyframes slide-in-left {
          from { transform: translateX(-110%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* FORCE sticky header */
        .sticky-header {
          position: sticky !important;
          position: -webkit-sticky !important;
          top: 0 !important;
          z-index: 9999 !important;
          background: ${isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(229, 231, 235, 0.9)'} !important;
          backdrop-filter: blur(20px) !important;
          -webkit-backdrop-filter: blur(20px) !important;
          border-bottom: 1px solid ${isDarkMode ? 'rgba(71, 85, 105, 0.3)' : 'rgba(209, 213, 219, 0.5)'} !important;
        }
        
        @keyframes heartbeat-pulse {
          0% { color: ${isDarkMode ? '#9ca3af' : '#6b7280'}; }
          25% { color: #eb008b; filter: drop-shadow(0 0 4px #eb008b); }
          50% { color: #eb008b; filter: drop-shadow(0 0 6px #eb008b); }
          50.1% { color: ${isDarkMode ? '#9ca3af' : '#6b7280'}; filter: none; }
          100% { color: ${isDarkMode ? '#9ca3af' : '#6b7280'}; filter: none; }
        }
        @keyframes spin-in {
          from { transform: rotate(-90deg) scale(0.8); opacity: 0; }
          to { transform: rotate(0deg) scale(1); opacity: 1; }
        }
        @keyframes spin-out {
          from { transform: rotate(0deg) scale(1); opacity: 1; }
          to { transform: rotate(-90deg) scale(0.8); opacity: 0; }
        }
        .spin-animation {
          animation: spin-in 0.3s ease-out forwards;
        }
        .spin-out-animation {
          animation: spin-out 0.3s ease-out forwards;
        }
        
        /* Menu animations */
        @keyframes menu-pop-in {
          from { 
            transform: translateY(-20px) scale(0.95); 
            opacity: 0; 
          }
          to { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }
        @keyframes menu-pop-out {
          from { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
          to { 
            transform: translateY(-20px) scale(0.95); 
            opacity: 0; 
          }
        }
        .menu-open {
          animation: menu-pop-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .menu-closing {
          animation: menu-pop-out 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        
        @keyframes chat-slide-in {
          from { transform: translateY(20px) scale(0.95); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes chat-slide-out {
          from { transform: translateY(0) scale(1); opacity: 1; }
          to { transform: translateY(20px) scale(0.95); opacity: 0; }
        }
        .chat-open {
          animation: chat-slide-in 0.3s ease-out forwards;
        }
        .chat-closing {
          animation: chat-slide-out 0.3s ease-out forwards;
        }
        @keyframes metrics-slide-in {
          from { transform: translateY(-20px) scale(0.95); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes metrics-slide-out {
          from { transform: translateY(0) scale(1); opacity: 1; }
          to { transform: translateY(-20px) scale(0.95); opacity: 0; }
        }
        .metrics-open {
          animation: metrics-slide-in 0.3s ease-out forwards;
        }
        .metrics-closing {
          animation: metrics-slide-out 0.3s ease-out forwards;
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 0.4; }
          100% { transform: scale(1.2); opacity: 0; }
        }
        @keyframes counter-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes progress-fill {
          from { stroke-dasharray: 0 251.2; }
          to { stroke-dasharray: 238.6 251.2; }
        }
        @keyframes fade-in-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .pulse-ring {
          animation: pulse-ring 2s infinite;
        }
        .counter-animate {
          animation: counter-up 0.8s ease-out;
        }
        .progress-circle {
          animation: progress-fill 2s ease-out forwards;
        }
        .portfolio-item {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        .portfolio-item:nth-child(1) { animation-delay: 0.1s; }
        .portfolio-item:nth-child(2) { animation-delay: 0.2s; }
        .portfolio-item:nth-child(3) { animation-delay: 0.3s; }
        .portfolio-item:nth-child(4) { animation-delay: 0.4s; }
        .portfolio-item:nth-child(5) { animation-delay: 0.5s; }
        .portfolio-item:nth-child(6) { animation-delay: 0.6s; }

        @keyframes title-fade-in {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes subtitle-fade-in {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes underline-expand {
          from { width: 0; opacity: 0; }
          to { width: 3rem; opacity: 1; }
        }
        .title-animate {
          animation: title-fade-in 0.8s ease-out forwards;
        }
        .subtitle-animate {
          animation: subtitle-fade-in 0.8s ease-out 0.3s forwards;
          opacity: 0;
        }
        .underline-animate {
          animation: underline-expand 0.6s ease-out 0.6s forwards;
          width: 0;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${isDarkMode ? 'rgba(71, 85, 105, 0.3)' : 'rgba(229, 231, 235, 0.3)'};
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(235, 0, 139, 0.5);
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(235, 0, 139, 0.7);
        }

        @keyframes particle-float {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(-60px) rotate(360deg);
            opacity: 0;
          }
        }
        .particle {
          animation: particle-float 2s ease-out forwards;
          pointer-events: none;
        }

        /* Notification animations */
        @keyframes notification-slide-in {
          from { 
            transform: translateX(100%); 
            opacity: 0; 
          }
          to { 
            transform: translateX(0); 
            opacity: 1; 
          }
        }
        @keyframes notification-slide-out {
          from { 
            transform: translateX(0); 
            opacity: 1; 
          }
          to { 
            transform: translateX(100%); 
            opacity: 0; 
          }
        }
        .notification-enter {
          animation: notification-slide-in 0.3s ease-out forwards;
        }
        .notification-exit {
          animation: notification-slide-out 0.3s ease-out forwards;
        }

        /* Hero video text animation */
        .duration-2000 {
          transition-duration: 2000ms;
        }

        @media screen and (max-width: 768px) {
          html {
            background-color: ${isDarkMode ? '#1e293b' : '#ffffff'};
            -webkit-text-size-adjust: 100%;
          }
          
          body {
            background-color: ${isDarkMode ? '#1e293b' : '#ffffff'};
            -webkit-font-smoothing: antialiased;
          }
          
          input, textarea, select {
            font-size: 16px !important;
          }
        }
      `}</style>
      
      {/* Background patterns and gradients - only show in dark mode */}
      {isDarkMode && (
        <>
          <div className="absolute inset-0 opacity-5 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden" style={{
              backgroundImage: `
                radial-gradient(circle at 1px 1px, white 1px, transparent 0),
                linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%),
                linear-gradient(-45deg, transparent 40%, rgba(255,255,255,0.05) 50%, transparent 60%)
              `,
              backgroundSize: '40px 40px, 80px 80px, 120px 120px',
              backgroundPosition: 'center, center, center'
            }}></div>
          </div>

          <div className="absolute inset-0 opacity-5 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
              <div className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl opacity-10" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl opacity-7" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute top-1/3 right-1/4 w-48 h-48 rounded-full blur-2xl opacity-12" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute bottom-1/3 left-1/4 w-56 h-56 rounded-full blur-2xl opacity-10" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute top-16 right-1/3 w-24 h-24 rounded-full blur-xl opacity-15" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute bottom-16 left-1/3 w-32 h-32 rounded-full blur-xl opacity-12" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute top-1/2 left-16 w-20 h-20 rounded-full blur-xl opacity-10" style={{backgroundColor: '#eb008b'}}></div>
              <div className="absolute top-1/4 right-16 w-28 h-28 rounded-full blur-xl opacity-12" style={{backgroundColor: '#eb008b'}}></div>
            </div>
          </div>
        </>
      )}


      {/* Toast notifications */}
      {showSignInSuccess && ReactDOM.createPortal(
        <div style={{
          position:'fixed', top:'80px', left:'50%', transform:'translateX(-50%)',
          background:'rgba(22,163,74,0.95)', backdropFilter:'blur(12px)',
          padding:'clamp(10px,2vw,16px) clamp(14px,3vw,24px)',
          borderRadius:'16px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
          display:'flex', alignItems:'center', gap:'10px',
          zIndex:2147483647, whiteSpace:'nowrap',
          animation:'scToastAnim 4s ease forwards'
        }}>
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          <span style={{color:'white',fontSize:'clamp(13px,3vw,16px)',fontWeight:600,fontFamily:'sans-serif'}}>Signed in successfully</span>
        </div>, document.getElementById('sc-signin-toast-container')
      )}

      {/* Header Components */}
      <TopStatusBar 
        theme={theme}
        isDarkMode={isDarkMode}
        serverStatus={serverStatus}
        servicesAvailability={services.length > 0 ? services[0].uptime : null}
        handleServicesToggle={handleStatusToggle}
        handleMetricsToggle={handleStatusToggle}
        onLoginClick={() => user ? openDashboard() : setShowLogin(true)}
        onContactClick={() => { if (showDashboard) { closeDashboard(); setTimeout(() => scrollToContact(), 100); } else scrollToContact(); }}
      />

      <MainHeader 
        theme={theme}
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        isMenuOpen={isMenuOpen}
        menuClosing={menuClosing}
        handleMenuToggle={handleMenuToggle}
        handleServicesClick={handleServicesClick}
        handleProjectsClick={handleProjectsClick}
        handleContactClick={handleContactClick}
        showLogin={showLogin}
        setShowLogin={setShowLogin}
        closeLogin={closeLogin}
        loginClosing={loginClosing}
        setShowDashboard={openDashboard}
        onSignInSuccess={triggerSignInSuccess}
        onSignOutSuccess={triggerSignOutSuccess}
        closeDashboard={closeDashboard}
      />

      <div className="relative z-10">
        {(showDashboard || dashboardTransitioning) && (
          <div style={{
            opacity: dashboardVisible ? 1 : 0,
            transform: dashboardVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.35s ease, transform 0.35s ease',
            pointerEvents: dashboardVisible ? 'auto' : 'none'
          }}>
            <ClientDashboard theme={theme} isDarkMode={isDarkMode} onClose={closeDashboard} onSignOutSuccess={triggerSignOutSuccess} />
          </div>
        )}
        {!showDashboard && (
          <div style={{
            opacity: dashboardTransitioning ? 0 : 1,
            transform: dashboardTransitioning ? 'translateY(-16px)' : 'translateY(0)',
            transition: 'opacity 0.35s ease, transform 0.35s ease'
          }}>
        <main className="px-6 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Content Sections */}
            <HeroVideoSection 
              showHeroText={showHeroText}
              setShowHeroText={setShowHeroText}
              theme={theme}
            />

            <PortfolioSection 
              id="projects"
              title="Premier Stadium Solutions"
              subtitle="Built for the biggest stages"
              items={portfolioItems}
              theme={theme}
              isDarkMode={isDarkMode}
              hoveredItem={hoveredItem}
              setHoveredItem={setHoveredItem}
              currentImageIndex={currentImageIndex}
              particles={particles}
              createParticles={createParticles}
            />

            <PortfolioSection 
              id="live-events"
              title="Live Event Management"
              subtitle="Spectacular productions for legendary performances"
              items={liveEventsPortfolioItems}
              theme={theme}
              isDarkMode={isDarkMode}
              hoveredItem={hoveredItem}
              setHoveredItem={setHoveredItem}
              currentImageIndex={currentImageIndex}
              particles={particles}
              createParticles={createParticles}
            />

            <PortfolioSection 
              id="promotions"
              title="Creative & Marketing"
              subtitle="Strategic campaigns for leading brands"
              items={promotionsPortfolioItems}
              theme={theme}
              isDarkMode={isDarkMode}
              hoveredItem={hoveredItem}
              setHoveredItem={setHoveredItem}
              currentImageIndex={currentImageIndex}
              particles={particles}
              createParticles={createParticles}
            />

            <PortfolioSection 
              id="music"
              title="Music & Entertainment"
              subtitle="Elevating artists to higher levels"
              items={musicPortfolioItems}
              theme={theme}
              isDarkMode={isDarkMode}
              hoveredItem={hoveredItem}
              setHoveredItem={setHoveredItem}
              currentImageIndex={currentImageIndex}
              particles={particles}
              createParticles={createParticles}
            />

            <ContactSection 
              theme={theme}
              isDarkMode={isDarkMode}
              contactForm={contactForm}
              handleContactFormChange={handleContactFormChange}
              handleContactSubmit={handleContactSubmit}
            />
          </div>
        </main>
        </div>
        )}
      </div>

      <Footer theme={theme} isDarkMode={isDarkMode} />

      {/* Widget Components - Now using the FIXED chat component */}
      <SystemStatusPanel
        theme={theme}
        isDarkMode={isDarkMode}
        statusOpen={statusOpen}
        statusClosing={statusClosing}
        handleStatusToggle={handleStatusToggle}
        uptimeData={uptimeData}
        services={services}
        servicesLoading={servicesLoading}
      />

      <ScrollToTopButton 
        showScrollTop={showScrollTop}
        scrollToTop={scrollToTop}
        theme={theme}
        isDarkMode={isDarkMode}
      />

      <ChatWidget 
        theme={theme}
        isDarkMode={isDarkMode}
        serverStatus={serverStatus}
        clerkUser={user}
      />

      <NotificationToast 
        notification={notification}
        setNotification={setNotification}
        theme={theme}
      />
    </div>
  );
};

export default App;